import {
  mapFromSetting,
  mapFromSettingToUserSetting,
  mapFromSettings,
  mapFromSettingsToUserSettings,
  mapToSetting,
} from 'mappings/settingMapping';
import SettingDTO from 'models/dto/settingDTO';
import Setting, { Settings } from 'models/setting';
import UserSetting, { UserSettings } from 'models/userSetting';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetSettings(accessToken: string): Promise<Settings> {
  try {
    const ar = await http.get<SettingDTO[]>('/settings', http.getRequestConfig(accessToken));

    return mapFromSettings(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetSetting(setting: string, accessToken: string): Promise<Setting> {
  try {
    const ar = await http.get<SettingDTO>(`/settings/${setting}`, http.getRequestConfig(accessToken));

    return mapFromSetting(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetSettingTopLevelTenant(setting: string, accessToken: string): Promise<Setting> {
  try {
    const ar = await http.get<SettingDTO>(`/settings/${setting}/top`, http.getRequestConfig(accessToken));

    return mapFromSetting(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function apiSetSetting(setting: string, value: any, accessToken: string): Promise<Setting> {
  try {
    const newSetting = new Setting();
    newSetting.setValue(setting, value);
    const settingDTO = mapToSetting(newSetting);
    const ar = await http.put<SettingDTO>('/settings', settingDTO, http.getRequestConfig(accessToken));

    return mapFromSetting(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function apiSetUserSetting(setting: string, value: any, accessToken: string): Promise<UserSetting> {
  try {
    const newSetting = new UserSetting();
    newSetting.setValue(setting, value);
    const settingDTO = mapToSetting(newSetting);
    const ar = await http.put<SettingDTO>('/settings/user', settingDTO, http.getRequestConfig(accessToken));

    return mapFromSettingToUserSetting(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetCurrentUserSettings(accessToken: string): Promise<UserSettings> {
  try {
    const ar = await http.get<SettingDTO[]>('/settings/user', http.getRequestConfig(accessToken));

    return mapFromSettingsToUserSettings(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function apiSetUserSettings(setting: string, value: any, accessToken: string): Promise<void> {
  try {
    const newSetting = new UserSetting();
    newSetting.setValue(setting, value);
    const settingDTO = mapToSetting(newSetting);
    await http.put('/settings/user/all', settingDTO, http.getRequestConfig(accessToken));

    return;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetUserSettings(accessToken: string): Promise<UserSettings> {
  try {
    const ar = await http.get<SettingDTO[]>('/settings/user/all', http.getRequestConfig(accessToken));

    return mapFromSettingsToUserSettings(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//
// Tenant admin
//
export async function apiGetTenantSettings(tenantId: string, accessToken: string): Promise<Settings> {
  try {
    const ar = await http.get<SettingDTO[]>(`/settings/tenant/${tenantId}`, http.getRequestConfig(accessToken));

    return mapFromSettings(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTenantSetting(tenantId: string, setting: string, accessToken: string): Promise<Setting> {
  try {
    const ar = await http.get<SettingDTO>(
      `/settings/${setting}/tenant/${tenantId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromSetting(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiSetTenantSetting(
  tenantId: string,
  setting: string,
  value: unknown,
  accessToken: string,
): Promise<Setting> {
  try {
    const newSetting = new Setting();
    newSetting.setValue(setting, value);
    const settingDTO = mapToSetting(newSetting);
    const ar = await http.put<SettingDTO>(
      `/settings/tenant/${tenantId}`,
      settingDTO,
      http.getRequestConfig(accessToken),
    );

    return mapFromSetting(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Writes the raw string value as-is: Setting.setValue only knows settings catalogued in the client,
//while the tenant admin settings grid must be able to save every record the backend returns.
export async function apiSetTenantSettingRaw(tenantId: string, setting: Setting, accessToken: string): Promise<Setting> {
  try {
    const settingDTO = mapToSetting(setting);
    const ar = await http.put<SettingDTO>(
      `/settings/tenant/${tenantId}`,
      settingDTO,
      http.getRequestConfig(accessToken),
    );

    return mapFromSetting(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
